import * as THREE from 'three';

/**
 * GPU crowd: animations are baked once into a float texture of per-bone skinning matrices
 * (one row per frame, 4 texels per bone), and every copy of an avatar is drawn as one
 * InstancedMesh whose vertex shader samples that texture. Per person the CPU only writes an
 * instance matrix and an animation slot, so the cost of the crowd no longer grows with the
 * number of skeletons, and an avatar costs one draw call however many people wear it.
 */

const BAKE_FPS = 30;

export interface BakedClip {
  name: string;
  row: number; // first row in the texture
  frames: number;
  duration: number;
}

export interface BakedAvatar {
  meshes: { geometry: THREE.BufferGeometry; material: THREE.Material }[];
  texture: THREE.DataTexture;
  clips: Map<string, BakedClip>;
}

/**
 * Sample every clip on a private clone of the avatar and store, for each frame and bone,
 * M = bone.matrixWorld * boneInverse * bindMatrix (avatar space). Skinning in the shader is
 * then just sum(w_i * M_i * position), with the instance matrix placing the person.
 */
export function bakeAvatar(scene: THREE.Object3D, clips: THREE.AnimationClip[], maxSeconds = 8): BakedAvatar {
  const root = scene.clone(true);
  root.position.set(0, 0, 0);
  root.rotation.set(0, 0, 0);
  root.scale.set(1, 1, 1);
  const skinned: THREE.SkinnedMesh[] = [];
  root.traverse((o) => { if ((o as THREE.SkinnedMesh).isSkinnedMesh) skinned.push(o as THREE.SkinnedMesh); });
  // clone(true) keeps the original skeleton references; rebind to the cloned bones by name
  const bonesByName = new Map<string, THREE.Bone>();
  root.traverse((o) => { if ((o as THREE.Bone).isBone) bonesByName.set(o.name, o as THREE.Bone); });
  const ref = skinned[0];
  const bones = ref.skeleton.bones.map((b) => bonesByName.get(b.name)!);
  const boneInverses = ref.skeleton.boneInverses;
  const bindMatrix = ref.bindMatrix;
  const B = bones.length;

  const plan = clips.map((c) => ({ clip: c, frames: Math.max(2, Math.round(Math.min(c.duration, maxSeconds) * BAKE_FPS)) }));
  const totalRows = plan.reduce((a, p) => a + p.frames, 0);
  const width = B * 4;
  const data = new Float32Array(width * totalRows * 4);
  const mixer = new THREE.AnimationMixer(root);
  const m = new THREE.Matrix4();
  const out = new Map<string, BakedClip>();

  let row = 0;
  for (const { clip, frames } of plan) {
    mixer.stopAllAction();
    const action = mixer.clipAction(clip);
    action.reset().play();
    for (let f = 0; f < frames; f++) {
      mixer.setTime(f / BAKE_FPS);
      root.updateMatrixWorld(true);
      for (let i = 0; i < B; i++) {
        m.multiplyMatrices(bones[i].matrixWorld, boneInverses[i]).multiply(bindMatrix);
        m.toArray(data, ((row + f) * width + i * 4) * 4);
      }
    }
    out.set(clip.name, { name: clip.name, row, frames, duration: frames / BAKE_FPS });
    row += frames;
    action.stop();
  }
  mixer.uncacheRoot(root);

  // (half floats: the matrices to a millimetre or so, half the memory; and once it's on the GPU the copy here goes: on a
  // phone the two share one memory, and ten of these as floats were 69 MB of it, twice)
  const half = new Uint16Array(data.length);
  for (let i = 0; i < data.length; i++) half[i] = THREE.DataUtils.toHalfFloat(data[i]);
  const texture = new THREE.DataTexture(half, width, totalRows, THREE.RGBAFormat, THREE.HalfFloatType);
  texture.needsUpdate = true;
  texture.onUpdate = () => { (texture.image as { data: unknown }).data = null; };
  return {
    meshes: skinned.map((s) => ({ geometry: s.geometry, material: s.material as THREE.Material })),
    texture,
    clips: out,
  };
}

/** Shader injection shared by the colour and shadow-depth materials. */
function injectCrowdSkinning(shader: THREE.WebGLProgramParametersWithUniforms, tex: THREE.DataTexture, time: { value: number }) {
  shader.uniforms.uAnimTex = { value: tex };
  shader.uniforms.uTime = time;
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', `#include <common>
      uniform highp sampler2D uAnimTex;
      uniform float uTime;
      attribute vec4 skinIndex;
      attribute vec4 skinWeight;
      attribute vec4 aAnim; // row, frames, time offset (s), time scale
      mat4 crowdBone(float bone, float row) {
        int x = int(bone) * 4;
        int y = int(row);
        return mat4(texelFetch(uAnimTex, ivec2(x, y), 0), texelFetch(uAnimTex, ivec2(x + 1, y), 0),
                    texelFetch(uAnimTex, ivec2(x + 2, y), 0), texelFetch(uAnimTex, ivec2(x + 3, y), 0));
      }
      // a function rather than inline code: the depth shader only includes the normal chunk
      // with displacement maps, so the skin matrix must be computable from begin_vertex alone
      mat4 crowdSkinMatrix() {
        float row = aAnim.x + mod(floor((uTime * aAnim.w + aAnim.z) * ${BAKE_FPS.toFixed(1)}), aAnim.y);
        return crowdBone(skinIndex.x, row) * skinWeight.x + crowdBone(skinIndex.y, row) * skinWeight.y
             + crowdBone(skinIndex.z, row) * skinWeight.z + crowdBone(skinIndex.w, row) * skinWeight.w;
      }`)
    .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>
      // tiny bias so a zero-weight vertex can't normalize a zero vector into NaN
      objectNormal = normalize(mat3(crowdSkinMatrix()) * objectNormal + vec3(0.0, 1e-5, 0.0));`)
    .replace('#include <begin_vertex>', `#include <begin_vertex>
      transformed = (crowdSkinMatrix() * vec4(transformed, 1.0)).xyz;`);
}

export interface CrowdSlot {
  avatar: number;
  index: number;
}

/**
 * Instanced meshes for every baked avatar. Callers allocate slots, then each frame write a
 * transform and (when it changes) an animation clip per slot. What's drawn is chosen in `cull`:
 * the slots that pass are packed to the front of the instance buffers, the rest aren't drawn.
 */
export class Crowd {
  readonly group = new THREE.Group();
  private time = { value: 0 };
  private per: { meshes: THREE.InstancedMesh[]; anim: THREE.InstancedBufferAttribute; matrix: THREE.InstancedBufferAttribute; used: number; baked: BakedAvatar; mats: Float32Array; anims: Float32Array }[] = [];

  constructor(avatars: BakedAvatar[], capacity: number[]) {
    avatars.forEach((baked, a) => {
      const n = Math.max(1, capacity[a]);
      const anim = new THREE.InstancedBufferAttribute(new Float32Array(n * 4), 4);
      anim.setUsage(THREE.DynamicDrawUsage);
      // (one set of instance matrices for all of an avatar's meshes: body, head, hair are the same people)
      const matrix = new THREE.InstancedBufferAttribute(new Float32Array(n * 16), 16);
      matrix.setUsage(THREE.DynamicDrawUsage);
      const meshes = baked.meshes.map(({ geometry, material }) => {
        const geo = geometry.clone();
        geo.setAttribute('aAnim', anim);
        const mat = (material as THREE.MeshStandardMaterial).clone();
        mat.onBeforeCompile = (s) => injectCrowdSkinning(s, baked.texture, this.time);
        mat.customProgramCacheKey = () => 'crowd-skin';
        const depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, alphaTest: (mat as THREE.MeshStandardMaterial).alphaTest || 0, map: mat.alphaTest ? (mat as THREE.MeshStandardMaterial).map : null });
        depth.onBeforeCompile = (s) => injectCrowdSkinning(s, baked.texture, this.time);
        depth.customProgramCacheKey = () => 'crowd-skin-depth';
        const im = new THREE.InstancedMesh(geo, mat, n);
        im.instanceMatrix = matrix;
        im.customDepthMaterial = depth;
        im.castShadow = true;
        im.receiveShadow = true;
        im.frustumCulled = false; // instances are spread along the whole street (cull picks who's drawn)
        im.count = 0;
        im.visible = false;
        this.group.add(im);
        return im;
      });
      this.per.push({ meshes, anim, matrix, used: 0, baked, mats: new Float32Array(n * 16), anims: new Float32Array(n * 4) });
    });
  }

  allocate(avatar: number): CrowdSlot {
    const p = this.per[avatar];
    return { avatar, index: p.used++ };
  }

  setAnimation(slot: CrowdSlot, clip: string, timeOffset: number, timeScale = 1) {
    const p = this.per[slot.avatar];
    const c = p.baked.clips.get(clip);
    if (!c) return;
    p.anims.set([c.row, c.frames, timeOffset, timeScale], slot.index * 4);
  }

  setMatrix(slot: CrowdSlot, m: THREE.Matrix4) {
    m.toArray(this.per[slot.avatar].mats, slot.index * 16);
  }

  clipDuration(avatar: number, clip: string) {
    return this.per[avatar].baked.clips.get(clip)?.duration ?? 1;
  }

  update(dt: number) {
    this.time.value += dt;
  }

  /** Draw the people `show` passes (given where they stand), and nobody shrunk to nothing (out of shot, in a wall). */
  cull(show: (x: number, y: number, z: number) => boolean) {
    for (const p of this.per) {
      const M = p.matrix.array as Float32Array, A = p.anim.array as Float32Array, src = p.mats;
      let n = 0;
      for (let i = 0; i < p.used; i++) {
        const o = i * 16;
        if (src[o] === 0 && src[o + 1] === 0 && src[o + 2] === 0) continue; // (scale 0: hidden)
        if (!show(src[o + 12], src[o + 13], src[o + 14])) continue;
        M.set(src.subarray(o, o + 16), n * 16);
        A.set(p.anims.subarray(i * 4, i * 4 + 4), n * 4);
        n++;
      }
      for (const m of p.meshes) { m.count = n; m.visible = n > 0; }
      if (n) {
        p.matrix.clearUpdateRanges(); p.matrix.addUpdateRange(0, n * 16); p.matrix.needsUpdate = true;
        p.anim.clearUpdateRanges(); p.anim.addUpdateRange(0, n * 4); p.anim.needsUpdate = true;
      }
    }
  }
}
